import type { DocumentProfile, ValidationReport } from '@photovalidator/core';
import type {
  FaceDetectorStatus,
  ImageInfo,
  PreprocessResult,
  ValidationStage,
  ValidatorClient,
  WorkerRequest,
  WorkerResponse,
} from './types.js';

/**
 * Main-thread facade over the CV pipeline.
 *
 * Prefers a Web Worker (so the UI never blocks and the photo stays in a
 * separate realm); falls back to running the very same pipeline on the main
 * thread when workers are unavailable. Both paths expose one interface, so no
 * view ever knows which one is active.
 */

export class PipelineRequestError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'PipelineRequestError';
    this.code = code;
  }
}

/** `Omit` over a union loses the discriminant; this variant keeps it. */
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

interface Pending {
  resolve: (value: never) => void;
  reject: (error: Error) => void;
}

class WorkerValidatorClient implements ValidatorClient {
  private worker: Worker | null = null;
  private sequence = 0;
  private readonly pending = new Map<number, Pending>();
  private warmupState: FaceDetectorStatus | null = null;

  private ensureWorker(): Worker {
    if (this.worker) return this.worker;
    const worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (event: MessageEvent<WorkerResponse>) => this.handleMessage(event.data);
    worker.onerror = (event) => {
      const error = new PipelineRequestError(
        'worker_failed',
        `Воркер валидатора остановился: ${event.message || 'неизвестная ошибка'}`,
      );
      for (const [, pending] of this.pending) pending.reject(error);
      this.pending.clear();
      this.worker?.terminate();
      this.worker = null;
    };
    this.worker = worker;
    return worker;
  }

  private handleMessage(message: WorkerResponse): void {
    const pending = this.pending.get(message.id);
    if (!pending) return;
    this.pending.delete(message.id);
    if (message.type === 'error') {
      pending.reject(new PipelineRequestError(message.code, message.message));
      return;
    }
    if (message.type === 'ready') {
      this.warmupState = {
        state: message.faceModel,
        ...(message.reason ? { reason: message.reason } : {}),
      };
      (pending.resolve as (value: FaceDetectorStatus) => void)(this.warmupState);
      return;
    }
    if (message.type === 'preprocessed') {
      (pending.resolve as (value: PreprocessResult) => void)(message.result);
      return;
    }
    (pending.resolve as (value: { report: ValidationReport }) => void)(message.result);
  }

  private request<T>(payload: DistributiveOmit<WorkerRequest, 'id'>): Promise<T> {
    const worker = this.ensureWorker();
    this.sequence += 1;
    const id = this.sequence;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, {
        resolve: resolve as (value: never) => void,
        reject,
      });
      worker.postMessage({ ...payload, id } as WorkerRequest);
    });
  }

  async warmup(): Promise<FaceDetectorStatus> {
    if (this.warmupState) return this.warmupState;
    return this.request<FaceDetectorStatus>({ type: 'warmup' });
  }

  setSource(file: Blob, info: Omit<ImageInfo, 'width' | 'height'>): Promise<PreprocessResult> {
    return this.request<PreprocessResult>({ type: 'set-source', file, info });
  }





  async validate(
    documentId: string,
    profile: DocumentProfile,
    fastOnly = false,
    maxStage: ValidationStage = 'advanced',
  ): Promise<ValidationReport> {
    const { report } = await this.request<{ report: ValidationReport }>({
      type: 'validate',
      documentId,
      profile,
      fastOnly,
      maxStage,
    });
    return report;
  }

  async preview(bitmap: ImageBitmap, profile: DocumentProfile): Promise<ValidationReport> {
    const { report } = await this.request<{ report: ValidationReport }>({
      type: 'preview',
      bitmap,
      profile,
    });
    return report;
  }

  dispose(): void {
    this.worker?.terminate();
    this.worker = null;
    this.pending.clear();
  }
}

/** Same pipeline, no worker: used when `Worker` is unavailable or fails. */
class MainThreadValidatorClient implements ValidatorClient {
  private pipeline: import('./pipeline.js').PhotoPipeline | null = null;
  private loading: Promise<import('./pipeline.js').PhotoPipeline> | null = null;
  private async ensurePipeline(): Promise<import('./pipeline.js').PhotoPipeline> {
    if (this.pipeline) return this.pipeline;
    this.loading ??= (async () => {
      const [{ createMediaPipeFaceDetector }, { PhotoPipeline }] = await Promise.all([
        import('./faceDetector.js'),
        import('./pipeline.js'),
      ]);
      const detector = await createMediaPipeFaceDetector({
        modelUrl: import.meta.env.VITE_FACE_MODEL_URL || undefined,
        wasmRoot: import.meta.env.VITE_MEDIAPIPE_WASM_ROOT || undefined,
      });
      return new PhotoPipeline({ detector, analysisMaxSide: 720 });
    })();
    this.pipeline = await this.loading;
    return this.pipeline;
  }

  async warmup(): Promise<FaceDetectorStatus> {
    const pipeline = await this.ensurePipeline();
    return pipeline.faceModel;
  }

  async setSource(file: Blob, info: Omit<ImageInfo, 'width' | 'height'>): Promise<PreprocessResult> {
    const pipeline = await this.ensurePipeline();
    return pipeline.setSource(file, info);
  }





  async validate(
    _documentId: string,
    profile: DocumentProfile,
    fastOnly = false,
    maxStage: ValidationStage = 'advanced',
  ): Promise<ValidationReport> {
    const pipeline = await this.ensurePipeline();
    return pipeline.validate(profile, fastOnly, maxStage);
  }

  async preview(bitmap: ImageBitmap, profile: DocumentProfile): Promise<ValidationReport> {
    const pipeline = await this.ensurePipeline();
    return pipeline.preview(bitmap, profile);
  }

  dispose(): void {
    this.pipeline?.dispose();
    this.pipeline = null;
  }
}

/** Creates the best available client for this browser. */
export const createValidatorClient = (): ValidatorClient => {
  if (typeof Worker === 'undefined') return new MainThreadValidatorClient();
  return new WorkerValidatorClient();
};
